/**
 * Client for the admin-only Pilot Trends endpoint (feat-146).
 *
 * Contract: ADR-0001 §4 — `GET /api/pilot-trends`, `Depends(require_admin)`
 * (Corporate + Manager → 403; stricter than the existing pilot surface). The `/api`
 * prefix is the router mount; the route decorator omits it.
 *
 * The backend computes ALL metric math and serializes DECIMAL columns as numbers
 * (memory: feedback_metrics_single_source) — this client never `parseFloat`s and
 * never re-derives a score/attempt/close formula. Shift-anchored fields arrive as
 * `null` (never 0) on non-recorded store-days; `orders_same_date` (the halo /
 * total-volume series) is always present because it is recording-independent.
 * `ring` (POS AOV, dollars) and `ipt` (items per transaction) are likewise
 * recording-independent POS aggregates (ADR-0001 feat-152): present ⇔ the store
 * had ≥1 non-canceled order that day (control stores included), `null` only on a
 * zero-order day — never coverage-gated. `revenue` / `transactions` (feat-179) are
 * Ring's own numerator and denominator with exactly the same presence rule.
 *
 * Fake mode (`config.environment === 'fake'`, i.e. npm run start:fake / Jest):
 * returns a synthesized response so the page renders without a backend. The dummy
 * numbers are shaped to reconcile to the published benchmark headline (580
 * California ≈ 305.7, 1 California ≈ 121.2) and set an illustrative "today" of
 * 2026-07-28 so the trend is legible (the real Jul-7 sparse start is a QA state).
 */
import { config } from '../config/environment';
import { authHeader } from './authToken';
import { authedApiFetch } from './tokenRefresh';

/**
 * `'pre'` (feat-229) is the OPT-IN pre-period phase: it appears ONLY on a request
 * that passes `start` (see `getPilotTrends`). A caller that omits `start` gets the
 * byte-identical `benchmark / gap / pilot` response it always got (spec AC1).
 */
export type PilotPhaseName = 'pre' | 'benchmark' | 'pilot';
export type PilotGroup = 'test' | 'control';

/**
 * feat-229 — the long-view card's window start. A CONSTANT, not a picker (spec
 * "Out of scope"). Earliest POS order date for all four L&L stores is exactly
 * 2026-01-20 (confirmed against dev by the backend slice's AC0 run, 2026-08-08).
 */
export const PRE_PERIOD_START = '2026-01-20';
/** Last day of the pre-period — abuts the benchmark window, no gap between them. */
export const PRE_PERIOD_END = '2026-06-21';

export interface PilotWindow {
  start: string; // 'YYYY-MM-DD'
  end: string;
}

export interface PilotPhase {
  phase: PilotPhaseName | 'gap';
  start: string;
  end: string;
}

export interface PilotStore {
  location_id: string;
  name: string;
  group: PilotGroup;
  /** test stores only — the reference-line anchors; control omits both. */
  benchmark_upsell_score?: number;
  target_upsell_score?: number;
}

/**
 * One flat per-(store × date) row. Order-anchored `orders_same_date` is always a
 * number; every shift-anchored field is `null` on non-recorded days. Gap dates and
 * future (> today) dates are OMITTED entirely from `series`.
 */
export interface PilotSeriesRow {
  location_id: string;
  date: string;
  phase: PilotPhaseName;
  upsell_score: number | null;
  pp_score: number | null;
  att_score: number | null;
  orders_in_coverage: number | null;
  orders_same_date: number;
  order_close_rate: number | null;
  close_rate_by_criterion: Record<string, number> | null;
  recorded_attempts: number | null;
  coverage_pct: number | null;
  attempts_by_criterion: Record<string, number> | null;
  /**
   * POS Average Order Value in dollars (recording-independent; ADR-0001 feat-152).
   * Present ⇔ the store had ≥1 non-canceled order that day (control stores
   * included); `null` only on a zero-order day.
   */
  ring: number | null;
  /** POS items per transaction — recording-independent, same presence rule as `ring`. */
  ipt: number | null;
  /**
   * feat-179 — the store-level read. Sum of the store's current, non-canceled POS
   * order totals for that local day, in dollars. This is **Ring's numerator**:
   * `revenue / transactions === ring` on every store-day where all three are
   * present (spec AC6). Server-side `float()` cast — never re-parsed here.
   *
   * `null` — NEVER `0` — on a day with no orders (spec AC7). A zero would draw as
   * a store that did $0 of business; absent data must render as a line break.
   */
  revenue: number | null;
  /**
   * feat-179 — count of distinct current, non-canceled POS orders for that local
   * day. **Ring's denominator** (see `revenue`). Server-side `int()` cast.
   * `null`, never `0`, on a no-order day.
   */
  transactions: number | null;
}

export interface PilotTrendsResponse {
  window: PilotWindow;
  phases: PilotPhase[];
  stores: PilotStore[];
  series: PilotSeriesRow[];
}

/** Thrown on a 403 so the page can render the admin-only access-denied state. */
export class PilotTrendsForbiddenError extends Error {
  constructor() {
    super('Pilot Trends is available to Bespoke administrators only.');
    this.name = 'PilotTrendsForbiddenError';
  }
}

function backendUrl(path: string): string {
  const protocol =
    config.environment === 'fake' || config.environment === 'local' ? 'http' : 'https';
  return `${protocol}://${config.restaurantBackendHost}${path}`;
}

/**
 * Fetch the per-store-per-day pilot series. `end` is an optional testing override
 * for the rolling window end (default = today, PT, resolved by the backend).
 *
 * feat-229: `start` is an OPT-IN window extension. Omitting it must stay
 * byte-identical to the pre-feat-229 behaviour for every existing caller (spec
 * AC1) — hence a second optional positional param rather than a new options
 * object, and hence the query string is built the same way it always was when
 * `start` is absent. `start=2026-01-20` adds a leading `pre` phase to `phases`
 * and moves `window.start`; the backend clamps it to
 * `[2026-01-20, PILOT_TRENDS_WINDOW_END]`.
 */
export async function getPilotTrends(
  end?: string,
  start?: string,
): Promise<PilotTrendsResponse> {
  if (config.environment === 'fake') {
    return fakePilotTrends(start);
  }
  const qs = [
    ...(end ? [`end=${encodeURIComponent(end)}`] : []),
    ...(start ? [`start=${encodeURIComponent(start)}`] : []),
  ];
  const path = qs.length ? `/api/pilot-trends?${qs.join('&')}` : '/api/pilot-trends';
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...authHeader(),
  };
  const resp = await authedApiFetch(backendUrl(path), { method: 'GET', headers });
  if (resp.status === 403) {
    throw new PilotTrendsForbiddenError();
  }
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(`Pilot Trends API ${resp.status}: ${text}`);
  }
  return resp.json();
}

// ─────────────────────────── Fake-mode synthesis ───────────────────────────

const FAKE_PHASES: PilotPhase[] = [
  { phase: 'benchmark', start: '2026-06-22', end: '2026-07-01' }, // feat-151 ADR-0001: window start 06-18 → 06-22
  { phase: 'gap', start: '2026-07-02', end: '2026-07-05' },
  { phase: 'pilot', start: '2026-07-06', end: '2026-08-14' },
];
const FAKE_TODAY = '2026-07-28'; // illustrative; real today = 2026-07-07 (sparse — a QA state)

interface FakeStoreSpec extends PilotStore {
  base: number;
  covBench: number;
  baseTotal: number;
  /** benchmark-baseline POS AOV ($) and items-per-txn — lift in the pilot phase for test stores. */
  ringBase: number;
  iptBase: number;
}
const FAKE_STORES: FakeStoreSpec[] = [
  { location_id: 'fake-580cal', name: '580 California', group: 'test', benchmark_upsell_score: 305.7, target_upsell_score: 372.4, base: 305.7, covBench: 0.543, baseTotal: 150, ringBase: 19.5, iptBase: 2.35 },
  { location_id: 'fake-1cal', name: '1 California', group: 'test', benchmark_upsell_score: 121.2, target_upsell_score: 148.3, base: 121.2, covBench: 0.471, baseTotal: 165, ringBase: 17.8, iptBase: 2.25 },
  { location_id: 'fake-221mont', name: '221 Montgomery', group: 'control', base: 92, covBench: 0.44, baseTotal: 140, ringBase: 16.2, iptBase: 2.1 },
  { location_id: 'fake-citycenter', name: 'City Center', group: 'control', base: 78, covBench: 0.4, baseTotal: 120, ringBase: 15.4, iptBase: 2.05 },
];

const FAKE_CRITERIA = [
  'garnish_s_suggest_attach',
  'soup_anchor_sandwich_upsell',
  'salad_anchor_soup_upsell',
  'sandwich_anchor_soup_upsell',
  'drink_promotion',
];

// deterministic pseudo-random so fake data is stable across reloads and tests.
function rng(seed: number): () => number {
  let s = seed % 2147483647;
  if (s <= 0) s += 2147483646;
  for (let k = 0; k < 4; k++) s = (s * 16807) % 2147483647;
  return () => ((s = (s * 16807) % 2147483647) - 1) / 2147483646;
}

function fakeDates(): string[] {
  const out: string[] = [];
  const cur = new Date('2026-06-22T12:00:00'); // feat-151 ADR-0001: domain start aligns to new benchmark start 06-22
  const last = new Date('2026-08-14T12:00:00');
  while (cur <= last) {
    out.push(cur.toISOString().slice(0, 10));
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

function fakePhaseOf(date: string): PilotPhase['phase'] {
  for (const p of FAKE_PHASES) if (date >= p.start && date <= p.end) return p.phase;
  return 'gap';
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * feat-229 AC16 — the fake pre-period (2026-01-20 → 2026-06-21).
 *
 * Deliberately generated **Mon–Fri only**: the four L&L stores are closed at
 * weekends, so a weekend produces NO series row at all (spec "Domain facts"). A
 * fake generator that emitted 7 rows a week would make every weekly bucket report
 * 7 open days and would quietly teach the opposite of AC8.
 *
 * Two short weeks are baked in on purpose, because they are the two states AC9
 * exists for and a happy-path-only fixture cannot exercise them:
 *   • 2026-W04 is a 4-open-day week by construction — Jan 20 is a Tuesday.
 *   • Fri 2026-06-19 emits NO row for any store — a genuine zero-order day
 *     (confirmed in raw `pos_order` on dev, spec "Data condition"), which makes
 *     2026-W25 a 4-open-day week.
 * (The third short week, benchmark W27 = Jun 29 – Jul 1, falls out of the phase
 * boundary itself and needs no fixture help.)
 *
 * The pre-period is drawn ~FLAT on purpose. The card's whole claim is "five flat
 * months, then a step"; fake data with a pre-trend already climbing would make the
 * card look like it works while showing the one shape it exists to rule out.
 *
 * `revenue = ring × transactions` here exactly as in the benchmark/pilot rows, so
 * the Ring identity holds across the whole extended window.
 */
const FAKE_ZERO_ORDER_DAYS = new Set(['2026-06-19']);

function fakePreSeries(startIso: string): PilotSeriesRow[] {
  const out: PilotSeriesRow[] = [];
  const cur = new Date(`${startIso}T12:00:00`);
  const last = new Date(`${PRE_PERIOD_END}T12:00:00`);
  let i = 0;
  while (cur <= last) {
    const dow = cur.getDay();
    const date = cur.toISOString().slice(0, 10);
    cur.setDate(cur.getDate() + 1);
    if (dow === 0 || dow === 6) continue; // closed — no row, not a zero
    if (FAKE_ZERO_ORDER_DAYS.has(date)) continue; // real zero-order day — no row
    i += 1;

    FAKE_STORES.forEach((st, si) => {
      // Own rng namespace (offset 500_000) so the pre-period cannot perturb the
      // existing benchmark/pilot draws — those are asserted on by feat-179 tests.
      const r = rng(500000 + 1000 * (si + 1) + i * 7 + 3);
      // Flat with a whisper of drift + a slow seasonal wobble. NOT a pre-trend.
      const drift = 1 + i * 0.00012;
      const season = 1 + Math.sin(i / 9) * 0.006;
      const ring = round2(st.ringBase * 0.985 * drift * season * (0.985 + r() * 0.03));
      const ipt = round2(st.iptBase * 0.99 * (0.995 + r() * 0.01));
      const total = Math.round(st.baseTotal * (0.85 + r() * 0.3));
      const canceled = Math.min(total - 1, (i * 3 + si * 7) % 6);
      const transactions = total - canceled;
      out.push({
        location_id: st.location_id,
        date,
        phase: 'pre',
        orders_same_date: total,
        upsell_score: null,
        pp_score: null,
        att_score: null,
        orders_in_coverage: null,
        order_close_rate: null,
        close_rate_by_criterion: null,
        recorded_attempts: null,
        coverage_pct: null,
        attempts_by_criterion: null,
        ring,
        ipt,
        revenue: round2(ring * transactions),
        transactions,
      });
    });
  }
  return out;
}

function fakePilotTrends(start?: string): PilotTrendsResponse {
  const dates = fakeDates();
  const pilotStart = new Date('2026-07-06T12:00:00').getTime();
  const todayMs = new Date(`${FAKE_TODAY}T12:00:00`).getTime();
  const span = (todayMs - pilotStart) / 86400000;

  const series: PilotSeriesRow[] = [];
  dates.forEach((date, i) => {
    const phase = fakePhaseOf(date);
    if (phase === 'gap' || date > FAKE_TODAY) return; // contract omits gap + future

    FAKE_STORES.forEach((st, si) => {
      const r = rng(1000 * (si + 1) + i * 7 + 3);
      const dayMs = new Date(`${date}T12:00:00`).getTime();

      // total daily orders (recording-independent) — always present.
      const haloLift =
        phase === 'pilot' && st.group === 'test'
          ? 1.04 + 0.05 * Math.min(1, (dayMs - pilotStart) / (30 * 86400000))
          : 1;
      const total = Math.round(st.baseTotal * (0.85 + r() * 0.3) * haloLift);

      // Ring (AOV $) + IPT — recording-independent POS aggregates, present for
      // every store-day with orders (control stores included). The thesis
      // (upsell effort → higher IPT → higher Ring) shows as co-movement UP in
      // the pilot phase for test stores; controls stay ~flat.
      const riProgress = phase === 'pilot' ? Math.min(1, (dayMs - pilotStart) / (30 * 86400000)) : 0;
      const isTest = st.group === 'test';
      const ipt = round2(st.iptBase * (1 + (isTest ? 0.13 : 0.02) * riProgress) * (0.98 + r() * 0.04));
      const ring = round2(st.ringBase * (1 + (isTest ? 0.2 : 0.03) * riProgress) * (0.97 + r() * 0.06));

      // feat-179 — the store-level read (Revenue & Transactions). Ring's own
      // numerator and denominator, so spec AC11 holds BY CONSTRUCTION:
      // `revenue / transactions === ring` for every fake store-day. Fake data
      // that violated AC6 would mask a real inconsistency bug during QA, so
      // revenue is DERIVED from ring here rather than generated independently.
      //
      // ⚠ `transactions` is NOT `orders_same_date`. They are different order
      // populations, and they differ in TWO opposing ways (both verified in the
      // backend queries, not inferred from the spec prose):
      //   • `orders_same_date` (pilot_trends.py `fetch_pos_orders`) has NO
      //     `state <> 'canceled'` filter; `txn_count` (pilot_metrics.py) does →
      //     pushes `orders_same_date` UP.
      //   • `fetch_pos_orders` bounds the day with `BETWEEN … '<end> 23:59:59'`
      //     while `opened_at` is `DATETIME(3)`, so 23:59:59.001–.999 is EXCLUDED
      //     there but INCLUDED by ring/ipt's half-open day windows → pushes
      //     `orders_same_date` DOWN.
      //
      // So NEITHER ordering is an invariant of real data — a sub-second-late
      // order on a day with no cancellations gives `transactions >
      // orders_same_date`. Below is a deliberate FIXTURE CHOICE, not a law:
      // keeping fake `transactions` under `orders_same_date` means the two keys
      // can never be aliased without a test noticing. Emitting them EQUAL is the
      // thing to avoid — that would make the halo series and the Ring
      // denominator look interchangeable and would let a card bound to the wrong
      // field pass. Hence a usually-non-zero canceled count: the difference is
      // exercised on most days rather than merely permitted.
      //
      // Derived from the loop indices rather than a fresh `r()` draw on purpose:
      // consuming the rng here would shift every downstream value and silently
      // rewrite every OTHER chart's fake data.
      const canceled = Math.min(total - 1, (i * 3 + si * 7) % 6); // 0–5, mostly > 0
      const transactions = total - canceled;
      const revenue = round2(ring * transactions);

      // controls never record → shift-anchored null; some benchmark test days unrecorded.
      const nonRecorded =
        st.group === 'control' || (phase === 'benchmark' && r() < 0.14);

      const base: PilotSeriesRow = {
        location_id: st.location_id,
        date,
        phase: phase as PilotPhaseName,
        orders_same_date: total,
        upsell_score: null,
        pp_score: null,
        att_score: null,
        orders_in_coverage: null,
        order_close_rate: null,
        close_rate_by_criterion: null,
        recorded_attempts: null,
        coverage_pct: null,
        attempts_by_criterion: null,
        ring,
        ipt,
        revenue,
        transactions,
      };

      if (nonRecorded) {
        series.push(base);
        return;
      }

      let score: number;
      if (phase === 'benchmark') {
        score = st.base * (0.9 + r() * 0.2);
      } else {
        const t = Math.min(1, (dayMs - pilotStart) / 86400000 / span);
        const tgt = st.target_upsell_score ?? st.base;
        score = st.base + (tgt - st.base) * t * 1.05 * (0.92 + r() * 0.16);
      }
      score = round1(score);
      const ppShare = 0.36 + r() * 0.08;

      const cov = st.covBench * (phase === 'pilot' ? 0.95 + r() * 0.25 : 0.9 + r() * 0.2);
      const incov = Math.max(1, Math.round(total * cov));
      const attempts = Math.round(incov * (score / 100));

      const closeAll = Math.max(0.12, Math.min(0.72, 0.44 + (r() - 0.4) * 0.18));
      const closeByCrit: Record<string, number> = {};
      const attByCrit: Record<string, number> = {};
      FAKE_CRITERIA.forEach((c, ci) => {
        closeByCrit[c] = Math.max(0.08, Math.min(0.8, closeAll * (0.7 + ((ci * 7 + i) % 5) * 0.12)));
        attByCrit[c] = Math.round(attempts / FAKE_CRITERIA.length) + (ci % 2);
      });

      series.push({
        ...base,
        upsell_score: score,
        pp_score: round1(score * ppShare),
        att_score: round1(score * (1 - ppShare)),
        orders_in_coverage: incov,
        order_close_rate: Math.round(closeAll * 1000) / 1000,
        close_rate_by_criterion: closeByCrit,
        recorded_attempts: attempts,
        coverage_pct: Math.round(cov * 1000) / 1000,
        attempts_by_criterion: attByCrit,
      });
    });
  });

  const stores: PilotStore[] = FAKE_STORES.map((s) => ({
    location_id: s.location_id,
    name: s.name,
    group: s.group,
    ...(s.benchmark_upsell_score != null ? { benchmark_upsell_score: s.benchmark_upsell_score } : {}),
    ...(s.target_upsell_score != null ? { target_upsell_score: s.target_upsell_score } : {}),
  }));

  // feat-229 — the pre-period is PREPENDED, never substituted. The
  // benchmark/pilot rows above are emitted identically whether or not `start` was
  // passed, so the long view's `% of benchmark` baseline is computed from exactly
  // the same benchmark days the existing card uses (spec AC12), and a no-`start`
  // call stays byte-identical to the pre-feat-229 payload (spec AC1).
  const wantPre = !!start && start <= PRE_PERIOD_END;
  const preStart = start && start > PRE_PERIOD_START ? start : PRE_PERIOD_START;

  return {
    window: {
      start: wantPre ? preStart : '2026-06-22', // feat-151 ADR-0001: window start 06-18 → 06-22
      end: '2026-08-14',
    },
    phases: wantPre
      ? [{ phase: 'pre' as const, start: preStart, end: PRE_PERIOD_END }, ...FAKE_PHASES]
      : FAKE_PHASES,
    stores,
    series: wantPre ? [...fakePreSeries(preStart), ...series] : series,
  };
}
